import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, ChevronDown } from '../../icons';
import { listTeams, type TeamDto } from '../../api/project-teams';
import { listProjects, type TaskProjectDto } from '../../api/tasks';
import { DetailPageHeader } from './DetailPageHeader';
import { TeamProjectList } from './TeamProjectList';
import { confirmAndDeleteProject } from './confirmDeleteProject';
import { ProjectCreateSimpleModal } from './ProjectCreateSimpleModal';
import { useModalLeave } from '../../hooks/useModalLeave';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import './ProjectPage.css';

/** 팀 필터 값 — '' = 전체, 'personal' = 무소속(개인), 그 외 = teamId. */
type TeamFilter = string;

/**
 * ProjectsDetailPage (project-menu v2 — /project/projects) — 프로젝트 카드 드릴다운 상세.
 *
 * 설계 §3.3-B: 팀 필터 셀렉터(전체/개인/팀별, `?team=` 딥링크 초기값) + 진행률 목록
 * (기존 TeamProjectList 재사용) + [새 프로젝트]. 행 클릭 → /task?project= (기존 무수정).
 */
export function ProjectsDetailPage() {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [teams, setTeams] = useState<TeamDto[]>([]);
  const [projects, setProjects] = useState<TaskProjectDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const filter: TeamFilter = searchParams.get('team') ?? '';

  const createModal = useModalLeave(creating || null, 200);
  useBodyScrollLock(createModal.shouldRender);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [t, p] = await Promise.all([
        listTeams().catch(() => [] as TeamDto[]),
        listProjects(),
      ]);
      setTeams(t);
      setProjects(p);
    } catch {
      /* 목록 실패 시 빈 목록 표시 — TeamProjectList empty 상태가 안내. */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const scoped = useMemo(() => {
    if (filter === '') return projects;
    if (filter === 'personal') return projects.filter((p) => !p.teamId);
    return projects.filter((p) => p.teamId === filter);
  }, [projects, filter]);

  function setFilter(value: TeamFilter) {
    setSearchParams(value === '' ? {} : { team: value }, { replace: true });
  }

  const openProject = useCallback((projectId: string) => {
    navigate(`/task?project=${projectId}`);
  }, [navigate]);

  return (
    <div className="pj-page">
      <DetailPageHeader
        title={tProject('project.detail.projects.title')}
        actions={(
          <>
            <div className="pj-team-select-wrap pj-team-select-wrap--inline">
              <select
                className="pj-team-select"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                aria-label={tProject('project.detail.projects.team_filter_aria')}
              >
                <option value="">{tProject('project.detail.projects.filter_all')}</option>
                <option value="personal">{tProject('project.team.personal')}</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <ChevronDown size={14} className="pj-team-select-chevron" />
            </div>
            <button type="button" className="pj-button pj-button--primary" onClick={() => setCreating(true)}>
              <Plus size={14} />
              {tProject('project.btn.new_project')}
            </button>
          </>
        )}
      />

      {/* key={filter} — 필터 전환 시 리마운트로 행 스태거 진입 재생 (v2.10.2) */}
      <TeamProjectList
        key={filter || 'all'}
        projects={scoped}
        loading={loading}
        onOpen={openProject}
        onAddProject={() => setCreating(true)}
        onDelete={(p) => { void confirmAndDeleteProject(p).then((deleted) => { if (deleted) void refresh(); }); }}
      />

      {createModal.shouldRender && (
        <ProjectCreateSimpleModal
          teams={teams}
          initialTeamId={filter !== '' && filter !== 'personal' ? filter : null}
          isLeaving={createModal.isLeaving}
          onClose={() => setCreating(false)}
          onCreated={(_project, teamId) => {
            setCreating(false);
            setFilter(teamId ?? (filter === 'personal' ? 'personal' : ''));
            void refresh();
          }}
        />
      )}
    </div>
  );
}
